import { apiService } from './api';
import { BulkLengthPricingPayload, BulkPricingUpdatePayload, InsurancePrices } from '../types';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
}

export interface SearchParams {
  skip?: number;
  limit?: number;
  query?: string;
  column?: string;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
  signal?: AbortSignal;
  is_archived?: boolean;
  is_booking?: boolean;
  filters?: Record<string, string[]>;
}

export interface FilterOption {
  value: string;
  count: number;
}

export class CrudService<T = any, TCreate = any, TUpdate = any> {
  constructor(private endpoint: string) {}

  async getAll(params: SearchParams = {}): Promise<T[]> {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.sort_column) queryParams.append('sort_column', params.sort_column);
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
    if (params.query) queryParams.append('query', params.query);
    if (params.column) queryParams.append('search_column', params.column);
    if (params.is_archived !== undefined) queryParams.append('is_archived', params.is_archived.toString());
    if (params.is_booking !== undefined) queryParams.append('is_booking', params.is_booking.toString());
    // Add column filters as JSON-encoded string
    if (params.filters && Object.keys(params.filters).length > 0) {
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(params.filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }

    const url = `${this.endpoint}/?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<T[]>(url, config);
  }

  async search(query: string, column: string, params: SearchParams = {}): Promise<T[]> {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.sort_column) queryParams.append('sort_column', params.sort_column);
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
    if (column) queryParams.append('column', column);
    if (params.is_archived !== undefined) queryParams.append('is_archived', params.is_archived.toString());
    if (params.is_booking !== undefined) queryParams.append('is_booking', params.is_booking.toString());
    // Add column filters as JSON-encoded string
    if (params.filters && Object.keys(params.filters).length > 0) {
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(params.filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }

    const url = `${this.endpoint}/search/${encodeURIComponent(query)}?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<T[]>(url, config);
  }

  async getById(id: string): Promise<T> {
    return apiService.get<T>(`${this.endpoint}/${id}`);
  }

  async create(data: TCreate): Promise<T> {
    return apiService.post<T>(`${this.endpoint}/`, data);
  }

  async update(id: string, data: TUpdate): Promise<T> {
    return apiService.put<T>(`${this.endpoint}/${id}`, data);
  }

  async delete(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`${this.endpoint}/${id}`);
  }

  async bulkDelete(ids: string[]): Promise<{ message: string; deleted_count: number }> {
    return apiService.delete<{ message: string; deleted_count: number }>(`${this.endpoint}/bulk`, {
      data: ids
    });
  }

  async bulkArchive(ids: string[]): Promise<{ message: string; archived_count: number }> {
    return apiService.post<{ message: string; archived_count: number }>(`${this.endpoint}/bulk/archive`, ids);
  }

  async archiveByDate(beforeDate: string): Promise<{ message: string; archived_count: number }> {
    return apiService.post<{ message: string; archived_count: number }>(`${this.endpoint}/archive-by-date?before_date=${beforeDate}`, {});
  }

  async bulkUpdateVoyage(carIds: string[], voyageId: string): Promise<{ message: string; updated_count: number; skipped_loaded?: string[] }> {
    return apiService.patch<{ message: string; updated_count: number; skipped_loaded?: string[] }>(`${this.endpoint}/bulk/update-voyage`, {
      car_ids: carIds,
      voyage_id: voyageId
    });
  }

  async bulkUpdateConsignee(
    carIds: string[],
    consigneeId?: string,
    consigneeName?: string,
    consigneeAddress?: string
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/update-consignee`, {
      car_ids: carIds,
      consignee_id: consigneeId || undefined,
      consignee_name: consigneeName || undefined,
      consignee_address: consigneeAddress || undefined,
    });
  }

  async bulkUpdateShipper(
    carIds: string[],
    shipperId: string
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/update-shipper`, {
      car_ids: carIds,
      shipper_id: shipperId
    });
  }

  async combineBl(
    carIds: string[]
  ): Promise<{ message: string; bl_number: string; updated_count: number }> {
    return apiService.patch<{ message: string; bl_number: string; updated_count: number }>(`${this.endpoint}/bulk/combine-bl`, {
      car_ids: carIds,
    });
  }

  async separateBl(
    carIds: string[]
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/separate-bl`, {
      car_ids: carIds,
    });
  }

  async bulkSetLoaded(
    carIds: string[],
    loaded: boolean
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/set-loaded`, {
      car_ids: carIds,
      loaded
    });
  }

  async bulkUpdatePort(
    carIds: string[],
    portId: string
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/update-port`, {
      car_ids: carIds,
      port_id: portId
    });
  }

  async bulkUpdatePricing(
    carIds: string[],
    payload: BulkPricingUpdatePayload
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/update-pricing`, {
      car_ids: carIds,
      ...payload
    });
  }

  async bulkSetLengthPricing(
    carIds: string[],
    payload: BulkLengthPricingPayload
  ): Promise<{ message: string; updated_count: number; skipped_count?: number; skipped_car_ids?: string[] }> {
    return apiService.patch<{
      message: string;
      updated_count: number;
      skipped_count?: number;
      skipped_car_ids?: string[];
    }>(`${this.endpoint}/bulk/set-length-pricing`, {
      car_ids: carIds,
      price_per_meter: payload.price_per_meter,
      apply_to: payload.apply_to ?? 'auto'
    });
  }

  async bulkAddInsurance(carIds: string[]): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/add-insurance`, {
      car_ids: carIds
    });
  }

  async bulkRemoveInsurance(carIds: string[]): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/remove-insurance`, {
      car_ids: carIds
    });
  }

  async getByVoyage(voyageId: string, params: SearchParams = {}): Promise<PaginatedResponse<T>> {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.sort_column) queryParams.append('sort_column', params.sort_column);
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
    if (params.query) queryParams.append('search_term', params.query);
    if (params.column) queryParams.append('search_column', params.column);
    // Add column filters as JSON-encoded string
    if (params.filters && Object.keys(params.filters).length > 0) {
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(params.filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }

    const url = `${this.endpoint}/voyage/${voyageId}?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<PaginatedResponse<T>>(url, config);
  }

  async getIdsByVoyage(voyageId: string, filters?: Record<string, string[]>): Promise<string[]> {
    const queryParams = new URLSearchParams();
    if (filters && Object.keys(filters).length > 0) {
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }
    const url = `${this.endpoint}/voyage/${voyageId}/ids?${queryParams.toString()}`;
    const response = await apiService.get<{ ids: string[] }>(url);
    return response.ids;
  }

  async bulkChangeVoyageByChassis(
    chassisNumbers: string[],
    fromVoyageId: string,
    toVoyageId: string
  ): Promise<{ message: string; updated_count: number }> {
    return apiService.patch<{ message: string; updated_count: number }>(`${this.endpoint}/bulk/change-voyage-by-chassis`, {
      chassis_numbers: chassisNumbers,
      from_voyage_id: fromVoyageId,
      to_voyage_id: toVoyageId
    });
  }

  async bulkUpdateLengthByChassis(
    voyageId: string,
    items: Array<{ chassis: string; length: number }>
  ): Promise<{ message: string; updated_count: number; failed?: Array<{ chassis: string; reason: string }> }> {
    return apiService.patch<{ message: string; updated_count: number; failed?: Array<{ chassis: string; reason: string }> }>(
      `${this.endpoint}/bulk/update-length-by-chassis`,
      { voyage_id: voyageId, items }
    );
  }

  async getAllCarsWithRelations(params: SearchParams = {}): Promise<T[]> {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.query && params.column) {
      queryParams.append('search_term', params.query);
      queryParams.append('search_column', params.column);
    }
    if (params.sort_column) {
      queryParams.append('sort_column', params.sort_column);
      queryParams.append('sort_direction', params.sort_direction || 'asc');
    }
    // Add column filters as JSON-encoded string
    if (params.filters && Object.keys(params.filters).length > 0) {
      // Only include non-empty filter arrays
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(params.filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }

    const url = `${this.endpoint}/all-with-relations?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<T[]>(url, config);
  }

  async getFilterOptions(
    column: string,
    filters?: Record<string, string[]>,
    extraParams?: { is_archived?: boolean; is_booking?: boolean; voyage_id?: string },
    signal?: AbortSignal
  ): Promise<FilterOption[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('column', column);
    if (filters && Object.keys(filters).length > 0) {
      queryParams.append('filters', JSON.stringify(filters));
    }
    if (extraParams?.is_archived !== undefined) {
      queryParams.append('is_archived', extraParams.is_archived.toString());
    }
    if (extraParams?.is_booking !== undefined) {
      queryParams.append('is_booking', extraParams.is_booking.toString());
    }
    if (extraParams?.voyage_id) {
      queryParams.append('voyage_id', extraParams.voyage_id);
    }
    const url = `${this.endpoint}/filter-options?${queryParams.toString()}`;
    const config = signal ? { signal } : undefined;
    return apiService.get<FilterOption[]>(url, config);
  }

  async getByAttachedId(attachedId: string): Promise<T[]> {
    return apiService.get<T[]>(`${this.endpoint}/by-attached/${attachedId}`);
  }

  // Generic helper for focus API calls
  private buildFocusUrl(
    focusType: string,
    value: string | number,
    voyageId?: string,
    countBefore: number = 50,
    countAfter: number = 50
  ): string {
    const queryParams = new URLSearchParams();
    queryParams.append('count_before', countBefore.toString());
    queryParams.append('count_after', countAfter.toString());

    const encodedValue = typeof value === 'string' ? encodeURIComponent(value) : value;
    const basePath = voyageId
      ? `${this.endpoint}/voyage/${voyageId}/focus${focusType}/${encodedValue}`
      : `${this.endpoint}/focus${focusType}/${encodedValue}`;

    return `${basePath}?${queryParams.toString()}`;
  }

  // Reference number focus (special case - no prefix in URL)
  async getFocusedCars(
    voyageId: string, referenceNumber: number, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_reference_number: number }> {
    const url = this.buildFocusUrl('', referenceNumber, voyageId, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_reference_number: number }>(url);
  }

  async getFocusedCarsGlobal(
    referenceNumber: number, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_reference_number: number }> {
    const url = this.buildFocusUrl('', referenceNumber, undefined, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_reference_number: number }>(url);
  }

  // Chassis focus
  async getFocusedCarsByChassis(
    voyageId: string, chassisNumber: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_chassis_number: string }> {
    const url = this.buildFocusUrl('-chassis', chassisNumber, voyageId, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_chassis_number: string }>(url);
  }

  async getFocusedCarsByChassisGlobal(
    chassisNumber: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_chassis_number: string }> {
    const url = this.buildFocusUrl('-chassis', chassisNumber, undefined, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_chassis_number: string }>(url);
  }

  // Shipper focus
  async getFocusedCarsByShipper(
    voyageId: string, shipperName: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_shipper_name: string }> {
    const url = this.buildFocusUrl('-shipper', shipperName, voyageId, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_shipper_name: string }>(url);
  }

  async getFocusedCarsByShipperGlobal(
    shipperName: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_shipper_name: string }> {
    const url = this.buildFocusUrl('-shipper', shipperName, undefined, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_shipper_name: string }>(url);
  }

  // Consignee focus
  async getFocusedCarsByConsignee(
    voyageId: string, consigneeName: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_consignee_name: string }> {
    const url = this.buildFocusUrl('-consignee', consigneeName, voyageId, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_consignee_name: string }>(url);
  }

  async getFocusedCarsByConsigneeGlobal(
    consigneeName: string, countBefore: number = 50, countAfter: number = 50
  ): Promise<{ data: T[]; total: number; focused_consignee_name: string }> {
    const url = this.buildFocusUrl('-consignee', consigneeName, undefined, countBefore, countAfter);
    return apiService.get<{ data: T[]; total: number; focused_consignee_name: string }>(url);
  }

  async getArchived(params: SearchParams = {}): Promise<T[]> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.query && params.column) {
      queryParams.append('search_term', params.query);
      queryParams.append('search_column', params.column);
    }
    if (params.sort_column) {
      queryParams.append('sort_column', params.sort_column);
      queryParams.append('sort_direction', params.sort_direction || 'asc');
    }
    if (params.filters && Object.keys(params.filters).length > 0) {
      const activeFilters: Record<string, string[]> = {};
      for (const [key, values] of Object.entries(params.filters)) {
        if (values && values.length > 0) {
          activeFilters[key] = values;
        }
      }
      if (Object.keys(activeFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(activeFilters));
      }
    }

    const url = `${this.endpoint}/archived?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<T[]>(url, config);
  }

  async convertToRelated(
    carId: string,
    parentCarId: string,
    relationshipType: string,
    remarks?: string,
    additionalFees?: Record<string, unknown>
  ): Promise<{ message: string; related_car_id: string; chassis_number: string }> {
    return apiService.post<{ message: string; related_car_id: string; chassis_number: string }>(
      `${this.endpoint}/convert-to-related`,
      {
        car_id: carId,
        parent_car_id: parentCarId,
        relationship_type: relationshipType,
        remarks: remarks,
        additional_fees: additionalFees
      }
    );
  }

  async bulkCreateCars(
    voyageId: string,
    cars: Array<{
      chassis_number: string;
      name?: string;
      address?: string;
      booking_reference?: string;
      year?: number;
      freight?: number;
      length?: number;
      width?: number;
      height?: number;
      weight?: number;
      cbm?: number;
      prepaid_or_collect?: 'P' | 'C';
      collect?: number;
      remarks?: string;
      port_code?: string;
      shipper_code?: string;
      consignee_code?: string;
      make_code?: string;
      model_code?: string;
      manual_make_model?: string;
    }>,
    overrides?: string[],
    keepBoth?: string[]
  ): Promise<{
    message: string;
    created_count: number;
    updated_count: number;
    failed: Array<{ row: string; chassis: string; reason: string }>
  }> {
    return apiService.post<{
      message: string;
      created_count: number;
      updated_count: number;
      failed: Array<{ row: string; chassis: string; reason: string }>
    }>(`${this.endpoint}/bulk/create`, {
      voyage_id: voyageId,
      cars,
      overrides: overrides || [],
      keep_both: keepBoth || []
    });
  }

  async bulkCheckExisting(chassisNumbers: string[]): Promise<{
    existing: Array<{
      chassis_number: string;
      id: string;
      voyage_code: string | null;
      make_name: string | null;
      model_name: string | null;
      shipper_name: string | null;
      consignee_name: string | null;
      year: number | null;
    }>
  }> {
    return apiService.post(`${this.endpoint}/bulk/check-existing`, {
      chassis_numbers: chassisNumbers
    });
  }
}

// Entity-specific service instances
export const shipperService = new CrudService('/api/v1/shippers');
export const consigneeService = new CrudService('/api/v1/consignees');
export const portService = new CrudService('/api/v1/ports');
export const shippingLineService = new CrudService('/api/v1/shipping-lines');
export const makeService = new CrudService('/api/v1/makes');
export const modelService = new CrudService('/api/v1/models');

// Extended model service with make search
export const modelSearchService = {
  async searchWithMake(
    query: string = "",
    limit: number = 50,
    signal?: AbortSignal
  ): Promise<Array<{
    id: string;
    code: string;
    name: string;
    type: string;
    weight: number;
    length: number;
    width: number;
    height: number;
    cbm: number;
    make_id: string;
    make: {
      id: string;
      code: string;
      name: string;
    };
  }>> {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    params.append('limit', limit.toString());
    const url = `/api/v1/models/search-with-make?${params.toString()}`;
    const config = signal ? { signal } : undefined;
    return apiService.get(url, config);
  }
};

// Generic search helper for any service - uses getAll with query parameter
export async function searchEntities<T>(
  service: CrudService<T>,
  query: string = "",
  limit: number = 50,
  signal?: AbortSignal
): Promise<T[]> {
  return service.getAll({ query, limit, signal });
}
export const companyService = new CrudService('/api/v1/companies');
export const voyageService = new CrudService('/api/v1/voyages');

// Extended car service with chassis check
class CarCrudService extends CrudService {
  async checkChassisExists(chassisNumber: string, excludeId?: string): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (excludeId) queryParams.append('exclude_id', excludeId);
    const url = `/api/v1/cars/check-chassis/${encodeURIComponent(chassisNumber)}?${queryParams.toString()}`;
    return apiService.get<any[]>(url);
  }
}

export const carService = new CarCrudService('/api/v1/cars');
export const relatedCarService = new CrudService('/api/v1/related-cars');

// Application-wide settings shared by all users
export const settingsService = {
  async getInsurancePrices(): Promise<InsurancePrices> {
    return apiService.get<InsurancePrices>('/api/v1/settings/insurance');
  },

  async setInsurancePrices(prices: InsurancePrices): Promise<InsurancePrices> {
    return apiService.put<InsurancePrices>('/api/v1/settings/insurance', prices);
  },
};

// Specialized voyage dropdown service for fast dropdown loading
export interface VoyageDropdownItem {
  id: string;
  voyage_code: string;
  vessel_description: string;
  departure: string;
  currency: string;
}

export const voyageDropdownService = {
  async getAll(params: {
    skip?: number;
    limit?: number;
    query?: string;
    is_archived?: boolean;
    is_booking?: boolean;
    signal?: AbortSignal;
  } = {}): Promise<VoyageDropdownItem[]> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.query) queryParams.append('query', params.query);
    if (params.is_archived !== undefined) queryParams.append('is_archived', params.is_archived.toString());
    if (params.is_booking !== undefined) queryParams.append('is_booking', params.is_booking.toString());

    const url = `/api/v1/voyages/dropdown?${queryParams.toString()}`;
    const config = params.signal ? { signal: params.signal } : undefined;
    return apiService.get<VoyageDropdownItem[]>(url, config);
  }
};

// Archive counts service
export interface ArchiveCounts {
  voyages: number;
  bookings: number;
  cars: number;
}

export const getArchiveCounts = async (): Promise<ArchiveCounts> => {
  return apiService.get<ArchiveCounts>('/api/v1/voyages/archive/counts');
};
